import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Scan, CheckCircle, AlertCircle, Camera, Video } from 'lucide-react';

interface ScanQRFlowProps {
  onClose: () => void;
}

export function ScanQRFlow({ onClose }: ScanQRFlowProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'initial' | 'scanning' | 'verify' | 'access'>('initial');
  const [scannedData, setScannedData] = useState<any>(null);
  const [cameraPermission, setCameraPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanIntervalRef = useRef<number | null>(null);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    setScanning(false);
  };

  const requestCameraAccess = async () => {
    console.log('🎥 Requesting camera access...');
    
    // Check if mediaDevices is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError(t('features.qrHub.scanFlow.cameraNotSupported'));
      console.error('❌ MediaDevices API not available');
      return;
    }

    try {
      setError(null);
      console.log('📱 Calling getUserMedia...');
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera on mobile
      });
      
      console.log('✅ Camera access granted!', stream);
      streamRef.current = stream;
      setCameraPermission('granted');
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        console.log('▶️ Video playing');
      }
      
      setStep('scanning');
      setScanning(true);
      
      // Simulate QR detection (in production, use jsQR or similar library)
      scanIntervalRef.current = window.setInterval(() => {
        // This would be replaced with actual QR scanning logic
        // For demo purposes, we'll simulate a scan after 3 seconds
      }, 100);
      
    } catch (err: any) {
      console.error('❌ Camera access error:', err);
      setCameraPermission('denied');
      let errorMessage: string;
      if (err.name === 'NotAllowedError') {
        errorMessage = t('features.qrHub.scanFlow.permissionDenied');
      } else if (err.name === 'NotFoundError') {
        errorMessage = t('features.qrHub.scanFlow.noCameraFound');
      } else if (err.name === 'NotReadableError') {
        errorMessage = t('features.qrHub.scanFlow.cameraInUse');
      } else if (err.name === 'SecurityError') {
        errorMessage = t('features.qrHub.scanFlow.securityError');
      } else {
        errorMessage = t('features.qrHub.scanFlow.cameraErrorGeneric', { message: err.message || 'Please ensure your device has a camera and try again.' });
      }
      setError(errorMessage);
    }
  };

  const handleSimulateScan = () => {
    // Simulate QR scan after a delay
    setTimeout(() => {
      stopCamera();
      setScannedData({
        petName: 'Max',
        petBreed: 'Golden Retriever',
        accessType: 'Professional Update',
        dataScope: ['Identity', 'Health & Vaccines', 'Documents'],
        expiresAt: '2024-12-20T14:00:00',
        ownerName: 'John Smith',
        allowedActions: ['View Records', 'Add Vaccination', 'Upload Documents']
      });
      setStep('verify');
    }, 2000);
  };

  const handleGrantAccess = () => {
    setStep('access');
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border dark:border-white/8">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-white/8 flex-shrink-0">
          <h3 className="text-xl text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>{t('features.qrHub.scanFlow.title')}</h3>
          <button onClick={onClose} className="text-gray-400 dark:text-white/50 hover:text-gray-600 dark:hover:text-white/70">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-auto flex-1">
          {/* Initial Step */}
          {step === 'initial' && (
            <div className="text-center space-y-6">
              <div className="w-64 h-64 bg-gray-100 dark:bg-[#0F0E16] rounded-xl mx-auto flex items-center justify-center border-4 border-dashed border-gray-300 dark:border-white/20">
                <Camera className="w-32 h-32 text-gray-400 dark:text-white/30" />
              </div>
              
              <div>
                <h4 className="text-xl mb-2 text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>{t('features.qrHub.scanFlow.title')}</h4>
                <p className="text-gray-600 dark:text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {t('features.qrHub.scanFlow.chooseMethod')}
                </p>
              </div>

              <div className="space-y-3">
                {/* Demo Mode - Primary Option */}
                <button
                  onClick={handleSimulateScan}
                  className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  <Scan className="w-5 h-5" />
                  {t('features.qrHub.scanFlow.tryDemoScanner')}
                </button>

                {/* Camera Option - Secondary */}
                <button
                  onClick={requestCameraAccess}
                  className="w-full px-6 py-3 border-2 border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center justify-center gap-2"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  <Camera className="w-5 h-5" />
                  {t('features.qrHub.scanFlow.useRealCamera')}
                </button>
              </div>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 text-left">
                      <p className="text-sm text-red-800 dark:text-red-200 mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {error}
                      </p>
                      
                      <div className="bg-white/50 dark:bg-white/5 rounded p-3 mb-3">
                        <p className="text-xs text-red-900 dark:text-red-100 mb-2" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>
                          {t('features.qrHub.scanFlow.howToEnableCamera')}
                        </p>
                        <ul className="text-xs text-red-800 dark:text-red-200 space-y-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                          <li>{t('features.qrHub.scanFlow.enableStep1')}</li>
                          <li>{t('features.qrHub.scanFlow.enableStep2')}</li>
                          <li>{t('features.qrHub.scanFlow.enableStep3')}</li>
                          <li>{t('features.qrHub.scanFlow.enableStep4')}</li>
                        </ul>
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={requestCameraAccess}
                          className="px-4 py-2 bg-red-600 dark:bg-red-700 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-600 text-sm"
                          style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                        >
                          {t('features.qrHub.scanFlow.tryAgain')}
                        </button>
                        <button
                          onClick={() => setError(null)}
                          className="px-4 py-2 bg-gray-600 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 text-sm"
                          style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                        >
                          {t('features.qrHub.scanFlow.dismiss')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-200" style={{ fontFamily: 'Inter, sans-serif' }}>
                  💡 {t('features.qrHub.scanFlow.tip')}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/8 rounded-lg p-4">
                <h5 className="text-sm mb-2 text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>
                  {t('features.qrHub.scanFlow.howItWorks')}
                </h5>
                <ul className="text-sm text-gray-600 dark:text-white/70 space-y-1 text-left" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <li>• {t('features.qrHub.scanFlow.demoScannerDesc')}</li>
                  <li>• {t('features.qrHub.scanFlow.realCameraDesc')}</li>
                  <li>• {t('features.qrHub.scanFlow.worksBoth')}</li>
                  <li>• {t('features.qrHub.scanFlow.securePrivacy')}</li>
                </ul>
              </div>
            </div>
          )}

          {/* Scanning Step */}
          {step === 'scanning' && (
            <div className="text-center space-y-6">
              <div className="relative w-full max-w-md mx-auto">
                <video
                  ref={videoRef}
                  className="w-full aspect-square bg-black rounded-xl object-cover"
                  autoPlay
                  playsInline
                  muted
                />
                <div className="absolute inset-0 border-4 border-indigo-500 rounded-xl pointer-events-none">
                  <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-indigo-500 rounded-tl-xl"></div>
                  <div className="absolute top-0 right-0 w-12 h-12 border-t-4 border-r-4 border-indigo-500 rounded-tr-xl"></div>
                  <div className="absolute bottom-0 left-0 w-12 h-12 border-b-4 border-l-4 border-indigo-500 rounded-bl-xl"></div>
                  <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-indigo-500 rounded-br-xl"></div>
                  
                  {/* Scanning line animation */}
                  <div className="absolute inset-x-0 top-1/2 h-0.5 bg-indigo-500 animate-pulse"></div>
                </div>
              </div>
              
              <div>
                <h4 className="text-xl mb-2 text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
                  {t('features.qrHub.scanFlow.positionInFrame')}
                </h4>
                <p className="text-gray-600 dark:text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {t('features.qrHub.scanFlow.holdSteady')}
                </p>
              </div>

              <button
                onClick={handleSimulateScan}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('features.qrHub.scanFlow.simulateScan')}
              </button>

              <button
                onClick={() => {
                  stopCamera();
                  setStep('initial');
                }}
                className="w-full px-4 py-3 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-white/90"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('features.qrHub.scanFlow.cancel')}
              </button>

              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-500/30 rounded-lg p-4">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <p className="text-sm text-green-800 dark:text-green-200" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {t('features.qrHub.scanFlow.cameraActive')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Verify Step */}
          {step === 'verify' && scannedData && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
                </div>
                <h4 className="text-xl mb-2 text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>{t('features.qrHub.scanFlow.qrCodeVerified')}</h4>
                <p className="text-gray-600 dark:text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>{t('features.qrHub.scanFlow.reviewAccessDetails')}</p>
              </div>

              {/* Pet Profile Summary */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center text-3xl">
                    🐕
                  </div>
                  <div>
                    <h5 className="text-xl mb-1 text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>{scannedData.petName}</h5>
                    <div className="text-gray-600 dark:text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>{scannedData.petBreed}</div>
                  </div>
                </div>
                <div className="text-sm text-gray-600 dark:text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {t('features.qrHub.scanFlow.owner')}: {scannedData.ownerName}
                </div>
              </div>

              {/* Access Details */}
              <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-6">
                <h5 className="mb-4 text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>{t('features.qrHub.scanFlow.accessDetails')}</h5>
                
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-gray-600 dark:text-white/50 mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>{t('features.qrHub.scanFlow.accessType')}</div>
                    <div className="px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded inline-block" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {scannedData.accessType}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-gray-600 dark:text-white/50 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>{t('features.qrHub.scanFlow.availableData')}</div>
                    <div className="flex flex-wrap gap-2">
                      {scannedData.dataScope.map((scope: string, index: number) => (
                        <span key={index} className="px-3 py-1 bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white/70 rounded text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-gray-600 dark:text-white/50 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>{t('features.qrHub.scanFlow.allowedActions')}</div>
                    <div className="space-y-1">
                      {scannedData.allowedActions.map((action: string, index: number) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-gray-700 dark:text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                          <span>{action}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-gray-600 dark:text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>{t('features.qrHub.scanFlow.expires')}</div>
                    <div className="text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>{new Date(scannedData.expiresAt).toLocaleString()}</div>
                  </div>
                </div>
              </div>

              {/* Identity Verification */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-500/30 rounded-lg p-4">
                <h5 className="mb-3 text-yellow-900 dark:text-yellow-100" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>{t('features.qrHub.scanFlow.verifyIdentity')}</h5>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="identity"
                      value="logged-in"
                      defaultChecked
                      className="w-4 h-4 text-indigo-600"
                    />
                    <div>
                      <div className="text-gray-900 dark:text-white" style={{ fontFamily: 'Inter, sans-serif' }}>{t('features.qrHub.scanFlow.loggedInAs')}</div>
                      <div className="text-sm text-gray-600 dark:text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>{t('features.qrHub.scanFlow.verifiedProfessional')}</div>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="identity"
                      value="guest"
                      className="w-4 h-4 text-indigo-600"
                    />
                    <div>
                      <div className="text-gray-900 dark:text-white" style={{ fontFamily: 'Inter, sans-serif' }}>{t('features.qrHub.scanFlow.continueAsGuest')}</div>
                      <div className="text-sm text-gray-600 dark:text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>{t('features.qrHub.scanFlow.limitedAccess')}</div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('initial')}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-white/90"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.qrHub.scanFlow.cancel')}
                </button>
                <button
                  onClick={handleGrantAccess}
                  className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.qrHub.scanFlow.accessPetRecords')}
                </button>
              </div>
            </div>
          )}

          {/* Access Granted Step */}
          {step === 'access' && scannedData && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              
              <div>
                <h4 className="text-xl mb-2 text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>{t('features.qrHub.scanFlow.accessGranted')}</h4>
                <p className="text-gray-600 dark:text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {t('features.qrHub.scanFlow.accessGrantedDesc', { accessType: scannedData.accessType, petName: scannedData.petName })}
                </p>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-500/30 rounded-lg p-4">
                <div className="text-sm text-green-800 dark:text-green-200 space-y-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <div>✓ {t('features.qrHub.scanFlow.sessionStarted')}</div>
                  <div>✓ {t('features.qrHub.scanFlow.ownerNotified')}</div>
                  <div>✓ {t('features.qrHub.scanFlow.actionsLogged')}</div>
                </div>
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={onClose}
                  className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" 
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.qrHub.scanFlow.viewPetProfile')}
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-white/90"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.qrHub.scanFlow.close')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}